import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { tidbitsAPI, racesAPI } from '../../services/api';
import '../../App.css';

function ManageTidbits() {
  const navigate = useNavigate();
  const [tidbits, setTidbits] = useState([]);
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTidbit, setEditingTidbit] = useState(null);
  
  // Form state
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedRace, setSelectedRace] = useState('');
  const [selectedHorse, setSelectedHorse] = useState('');
  const [isPublished, setIsPublished] = useState(true);
  const [isPinned, setIsPinned] = useState(false);
  const [availableHorses, setAvailableHorses] = useState([]);
  const editFormRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  // Scroll the edit/create form into view whenever it becomes visible
  useEffect(() => {
    if (showForm && editFormRef.current) {
      editFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Focus the title input so the user can start typing immediately
      const titleInput = editFormRef.current.querySelector('#title');
      if (titleInput) titleInput.focus();
    }
  }, [showForm, editingTidbit]);

  useEffect(() => {
    // Load horses when race is selected
    if (selectedRace) {
      const race = races.find(r => r.raceId === selectedRace);
      setAvailableHorses(race?.horses || []);
    } else {
      setAvailableHorses([]);
      setSelectedHorse('');
    }
  }, [selectedRace, races]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load tidbits
      const tidbitsResponse = await tidbitsAPI.getAllTidbits({ includeUnpublished: true });
      if (tidbitsResponse.success) {
        setTidbits(tidbitsResponse.data.tidbits || []);
      }
      
      // Load races for the form
      const racesResponse = await racesAPI.getRaces();
      if (racesResponse.success) {
        setRaces(racesResponse.data.races || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingTidbit(null);
    setTitle('');
    setContent('');
    setSelectedRace('');
    setSelectedHorse('');
    setIsPublished(true);
    setIsPinned(false);
    setShowForm(true);
  };

  const handleEdit = (tidbit) => {
    setEditingTidbit(tidbit);
    setTitle(tidbit.title);
    setContent(tidbit.content);
    setSelectedRace(tidbit.raceId || '');
    setSelectedHorse(tidbit.horseId || '');
    setIsPublished(tidbit.isPublished);
    setIsPinned(tidbit.isPinned || false);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      alert('Title is required');
      return;
    }
    
    if (!content.trim()) {
      alert('Content is required');
      return;
    }

    try {
      const tidbitData = {
        title: title.trim(),
        content: content.trim(),
        raceId: selectedRace || null,
        horseId: selectedHorse || null,
        isPublished,
        isPinned
      };

      if (editingTidbit) {
        // Update existing
        await tidbitsAPI.updateTidbit(editingTidbit.tidbitId, tidbitData);
        alert('Tidbit updated successfully!');
      } else {
        // Create new
        await tidbitsAPI.createTidbit(tidbitData);
        alert('Tidbit created successfully!');
      }

      // Reset form and reload
      setShowForm(false);
      setEditingTidbit(null);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error?.message || 'Failed to save tidbit');
    }
  };

  const handleDelete = async (tidbitId, tidbitTitle) => {
    if (!window.confirm(`Delete tidbit "${tidbitTitle}"?`)) {
      return;
    }

    try {
      await tidbitsAPI.deleteTidbit(tidbitId);
      alert('Tidbit deleted successfully!');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error?.message || 'Failed to delete tidbit');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTidbit(null);
  };

  // Quill editor modules
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'link'
  ];

  if (loading) {
    return <div className="loading">Loading tidbits...</div>;
  }

  return (
    <div className="manage-tidbits">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>Manage Tidbits</h2>
        <button onClick={() => navigate('/admin')} className="submit-button" style={{ background: '#666' }}>
          ← Back to Admin
        </button>
      </div>

      {!showForm && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <button onClick={handleCreateNew} className="submit-button" style={{ width: '100%' }}>
            + Create New Tidbit
          </button>
        </div>
      )}

      {showForm && (
        <div ref={editFormRef} className="card" style={{ marginBottom: '2rem' }}>
          <h3>{editingTidbit ? 'Edit Tidbit' : 'Create New Tidbit'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="title" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Title *
              </label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter tidbit title"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
                required
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Content *
              </label>
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                modules={modules}
                formats={formats}
                placeholder="Write your tidbit content..."
                style={{ background: 'white', minHeight: '200px' }}
              />
            </div>

            <div style={{ marginBottom: '1rem', marginTop: '3rem' }}>
              <label htmlFor="race" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Associated Race (Optional)
              </label>
              <select
                id="race"
                value={selectedRace}
                onChange={(e) => setSelectedRace(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
              >
                <option value="">None (General Tidbit)</option>
                {races.map(race => (
                  <option key={race.raceId} value={race.raceId}>
                    {race.raceName} - {new Date(race.raceDate).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>

            {selectedRace && (
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="horse" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Associated Horse (Optional)
                </label>
                <select
                  id="horse"
                  value={selectedHorse}
                  onChange={(e) => setSelectedHorse(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                >
                  <option value="">None (Race-wide Tidbit)</option>
                  {availableHorses.map(horse => (
                    <option key={horse.horseId} value={horse.horseId}>
                      #{horse.postPosition} - {horse.horseName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                  style={{ marginRight: '0.5rem' }}
                />
                <span>Published (visible to users)</span>
              </label>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isPinned}
                  onChange={(e) => setIsPinned(e.target.checked)}
                  style={{ marginRight: '0.5rem' }}
                />
                <span>📌 Pin to Dashboard (shows as announcement)</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="submit" className="submit-button" style={{ flex: 1 }}>
                {editingTidbit ? 'Update Tidbit' : 'Create Tidbit'}
              </button>
              <button type="button" onClick={handleCancel} className="submit-button" style={{ flex: 1, background: '#666' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <h3>All Tidbits ({tidbits.length})</h3>
        {tidbits.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
            No tidbits yet. Create one to get started!
          </p>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {tidbits.map(tidbit => (
              <div
                key={tidbit.tidbitId}
                style={{
                  padding: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  background: tidbit.isPublished ? 'white' : '#f9f9f9'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 0.5rem 0' }}>
                      {tidbit.title}
                      {!tidbit.isPublished && (
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: '#f57c00' }}>
                          [Draft]
                        </span>
                      )}
                    </h4>
                    {tidbit.horseName && tidbit.raceName && (
                      <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#666' }}>
                        🏇 {tidbit.horseName} - {tidbit.raceName}
                      </p>
                    )}
                    {tidbit.raceName && !tidbit.horseName && (
                      <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#666' }}>
                        🏁 {tidbit.raceName}
                      </p>
                    )}
                    {!tidbit.raceName && (
                      <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#666' }}>
                        📝 General Tidbit
                      </p>
                    )}
                    <div
                      style={{ fontSize: '0.9rem', color: '#333', marginBottom: '0.5rem' }}
                      dangerouslySetInnerHTML={{ __html: tidbit.content.substring(0, 150) + '...' }}
                    />
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#999' }}>
                      By {tidbit.author} on {new Date(tidbit.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                    <button
                      onClick={() => handleEdit(tidbit)}
                      style={{
                        padding: '0.4rem 0.8rem',
                        background: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(tidbit.tidbitId, tidbit.title)}
                      style={{
                        padding: '0.4rem 0.8rem',
                        background: '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ManageTidbits;
